// src/contexts/PageTransitionContext.js
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const PageTransitionContext = createContext(null);

export const usePageTransition = () => {
  const context = useContext(PageTransitionContext);
  if (!context) {
    throw new Error('usePageTransition must be used within a PageTransitionProvider');
  }
  return context;
};

export const PageTransitionProvider = ({ children }) => {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [targetPage, setTargetPage] = useState('cadpro');
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [isExternalNavigation, setIsExternalNavigation] = useState(false);

  // Limpa o estado de transição quando a página é restaurada do cache (botão voltar)
  // ou quando a página é carregada pela primeira vez
  useEffect(() => {
    const handlePageShow = (event) => {
      // persisted indica que a página foi restaurada do bfcache (back/forward cache)
      if (event.persisted) {
        setIsTransitioning(false);
        setPendingNavigation(null);
        setIsExternalNavigation(false);
      }
    };

    // Também verifica se há um estado de transição pendente na inicialização
    // Isso pode acontecer se o usuário usar o botão voltar antes da página ser totalmente descarregada
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isExternalNavigation) {
        // Se a página se tornou visível e havia uma navegação externa pendente,
        // significa que o usuário voltou. Limpa o estado.
        setIsTransitioning(false);
        setPendingNavigation(null);
        setIsExternalNavigation(false);
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isExternalNavigation]);

  const startTransition = useCallback((target, navigationPath, isExternal = false) => {
    setTargetPage(target);
    setPendingNavigation(navigationPath);
    setIsExternalNavigation(isExternal);
    setIsTransitioning(true);
  }, []);

  const completeTransition = useCallback(() => {
    setIsTransitioning(false);
    const path = pendingNavigation;
    const isExternal = isExternalNavigation;
    setPendingNavigation(null);
    setIsExternalNavigation(false);
    return { path, isExternal };
  }, [pendingNavigation, isExternalNavigation]);

  return (
    <PageTransitionContext.Provider value={{
      isTransitioning,
      targetPage,
      pendingNavigation,
      isExternalNavigation,
      startTransition,
      completeTransition
    }}>
      {children}
    </PageTransitionContext.Provider>
  );
};
